/**
 * Alert Management — Commercial-Grade Intelligence Triage Centre
 * Features: status workflow, detail modal, batch ops, KPIs, advanced filtering, CSV export
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Bell, CheckCircle, XCircle, Filter, RefreshCw,
  Download, Search, User, Clock, Zap, Target,
  Eye, ArrowUpRight, Shield, Radio, X, Play,
  TrendingUp, Globe, Hash, Layers,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import type { MonitoringScope } from '../types';

interface AlertManagementProps {
  monitoringScope?: MonitoringScope;
}

// ── Types ─────────────────────────────────────────────────────────────────────
type Severity = 'CRITICAL' | 'WARNING' | 'INFO';
type Status   = 'new' | 'investigating' | 'contained' | 'escalated' | 'resolved';

interface Alert {
  id: string;
  type: string;
  severity: Severity;
  message: string;
  platform: string;
  timestamp: string;
  status: Status;
  source?: string;
  riskScore: number;
  velocity: number;
  reachEstimate: number;
  platforms: string[];
  assignee?: string;
  recommendedActions: string[];
  factors: string[];
  evidenceSnippets: string[];
  activityLog: { ts: string; actor: string; action: string }[];
}

// ── Mock data ─────────────────────────────────────────────────────────────────
const MOCK_ALERTS: Alert[] = [
  {
    id: 'a1', type: 'MISINFORMATION', severity: 'CRITICAL',
    message: 'Coordinated bot network amplifying election fraud claims — 3,400+ posts in 2 hours',
    platform: 'Twitter/X', timestamp: new Date(Date.now() - 12 * 60000).toISOString(),
    status: 'investigating', source: '@FakeNewsBot_Cluster',
    riskScore: 94, velocity: 1700, reachEstimate: 2_400_000,
    platforms: ['Twitter/X', 'Telegram', 'Facebook'], assignee: 'Maya R.',
    recommendedActions: ['Flag top 12 amplifier accounts for platform reporting', 'Cross-reference with known influence operation databases', 'Prepare rapid-response fact-check article', 'Notify partner media outlets with counter-narrative kit'],
    factors: ['Bot account signatures', 'Coordinated posting intervals', 'Identical caption templates', 'Velocity +820% vs baseline'],
    evidenceSnippets: ['"BREAKING: Thousands of ballots found in storage unit — election rigged!"', '"Share before they delete this!! Evidence of fraud finally exposed"'],
    activityLog: [{ ts: new Date(Date.now() - 12 * 60000).toISOString(), actor: 'System', action: 'Alert triggered — risk score 94' }, { ts: new Date(Date.now() - 9 * 60000).toISOString(), actor: 'Maya R.', action: 'Assigned to self · Status → Investigating' }],
  },
  {
    id: 'a2', type: 'BOT_NETWORK', severity: 'CRITICAL',
    message: 'Bot cluster detected: 47 accounts posting identical content within 90-second windows',
    platform: 'Telegram', timestamp: new Date(Date.now() - 28 * 60000).toISOString(),
    status: 'new', source: 'TG: TruthOrders',
    riskScore: 88, velocity: 940, reachEstimate: 890_000,
    platforms: ['Telegram', 'WhatsApp'],
    recommendedActions: ['Report cluster to Telegram abuse team', 'Map network graph to identify seed accounts', 'Monitor downstream platforms for cross-posting'],
    factors: ['Identical post timing (±3s)', 'Shared media hashes', 'New accounts < 14 days old', 'Velocity spike +460%'],
    evidenceSnippets: ['"Join the resistance — forward to 10 groups now!"'],
    activityLog: [{ ts: new Date(Date.now() - 28 * 60000).toISOString(), actor: 'System', action: 'Bot cluster signature matched — risk 88' }],
  },
  {
    id: 'a3', type: 'VIRAL_SPREAD', severity: 'WARNING',
    message: 'Unverified health claim gaining traction — 1,200 shares in 45 minutes',
    platform: 'Facebook', timestamp: new Date(Date.now() - 47 * 60000).toISOString(),
    status: 'new', source: 'HealthTruthNow',
    riskScore: 67, velocity: 320, reachEstimate: 520_000,
    platforms: ['Facebook', 'Instagram'],
    recommendedActions: ['Activate health fact-check queue', 'Request review from medical advisory board', 'Prepare clarifying content with health authorities'],
    factors: ['High organic share rate', 'No credible source citation', 'Cross-platform spread detected'],
    evidenceSnippets: ['"Doctors DON\'T want you to know — this herb cures COVID in 24h"'],
    activityLog: [{ ts: new Date(Date.now() - 47 * 60000).toISOString(), actor: 'System', action: 'Viral threshold crossed' }],
  },
  {
    id: 'a4', type: 'DEEP_FAKE', severity: 'CRITICAL',
    message: 'AI-generated video detected impersonating government official',
    platform: 'YouTube', timestamp: new Date(Date.now() - 73 * 60000).toISOString(),
    status: 'escalated', source: 'GovFakeVid2024',
    riskScore: 97, velocity: 2200, reachEstimate: 5_100_000,
    platforms: ['YouTube', 'Twitter/X', 'Facebook', 'TikTok'], assignee: 'Priya K.',
    recommendedActions: ['URGENT: Submit takedown request to YouTube immediately', 'Notify government communications office', 'Prepare official denial statement', 'Alert national cyber security incident team'],
    factors: ['Facial inconsistency markers', 'Audio artifact patterns', 'Known deepfake watermark absent', 'Velocity +1200%'],
    evidenceSnippets: ['"[Official] Emergency announcement — do not leave your homes"'],
    activityLog: [{ ts: new Date(Date.now() - 73 * 60000).toISOString(), actor: 'System', action: 'Deepfake detection model flagged — confidence 97%' }, { ts: new Date(Date.now() - 65 * 60000).toISOString(), actor: 'Priya K.', action: 'Escalated to Crisis Response Team' }],
  },
  {
    id: 'a5', type: 'NARRATIVE_SHIFT', severity: 'WARNING',
    message: 'Sudden spike in anti-vaccination sentiment across 3 platforms — coordinated push suspected',
    platform: 'Multi-platform', timestamp: new Date(Date.now() - 91 * 60000).toISOString(),
    status: 'investigating', source: 'CrossPlatform Monitor',
    riskScore: 71, velocity: 560, reachEstimate: 1_200_000,
    platforms: ['Twitter/X', 'Facebook', 'Reddit'], assignee: 'Leon M.',
    recommendedActions: ['Cross-reference with WHO rapid-response database', 'Identify origin account or seed content', 'Brief public health communication team'],
    factors: ['Sentiment shift +44% negative', 'Coordinated hashtag usage', 'Timing correlates with scheduled political event'],
    evidenceSnippets: ['"#NoMoreJabs trending — mainstream media won\'t report this"'],
    activityLog: [{ ts: new Date(Date.now() - 91 * 60000).toISOString(), actor: 'System', action: 'Sentiment drift detected' }, { ts: new Date(Date.now() - 80 * 60000).toISOString(), actor: 'Leon M.', action: 'Under investigation — monitoring 48h' }],
  },
  {
    id: 'a6', type: 'SOURCE_CREDIBILITY', severity: 'INFO',
    message: 'New misinformation source identified: gossipbreaking.net — credibility score 8/100',
    platform: 'Web', timestamp: new Date(Date.now() - 134 * 60000).toISOString(),
    status: 'resolved', source: 'gossipbreaking.net',
    riskScore: 42, velocity: 80, reachEstimate: 95_000,
    platforms: ['Web', 'Facebook'],
    recommendedActions: ['Add to low-credibility source registry', 'Enable auto-flag for any post citing this domain'],
    factors: ['Domain registered < 30 days', 'No editorial contact info', 'Cited by 3 known disinfo accounts'],
    evidenceSnippets: [],
    activityLog: [{ ts: new Date(Date.now() - 134 * 60000).toISOString(), actor: 'System', action: 'Source registered in low-credibility index' }, { ts: new Date(Date.now() - 110 * 60000).toISOString(), actor: 'System', action: 'Auto-resolved: domain added to blocklist' }],
  },
  {
    id: 'a7', type: 'THRESHOLD', severity: 'INFO',
    message: 'Post volume threshold exceeded for keyword "climate hoax" — 500% above baseline',
    platform: 'Reddit', timestamp: new Date(Date.now() - 182 * 60000).toISOString(),
    status: 'contained', source: 'r/conspiracy',
    riskScore: 38, velocity: 140, reachEstimate: 210_000,
    platforms: ['Reddit'],
    recommendedActions: ['Monitor subreddit for escalation', 'Log for trend analysis report'],
    factors: ['Keyword frequency spike', 'Concentrated in 2 subreddits'],
    evidenceSnippets: [],
    activityLog: [{ ts: new Date(Date.now() - 182 * 60000).toISOString(), actor: 'System', action: 'Keyword alert fired' }, { ts: new Date(Date.now() - 160 * 60000).toISOString(), actor: 'System', action: 'Volume returned to near-normal — marked contained' }],
  },
];

// ── Constants ─────────────────────────────────────────────────────────────────
const SEV_STYLE: Record<Severity, string> = {
  CRITICAL: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
  WARNING:  'text-amber-400 bg-amber-500/10 border-amber-500/30',
  INFO:     'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
};

const STATUS_STYLE: Record<Status, string> = {
  new:          'text-violet-400 bg-violet-500/10 border-violet-500/30',
  investigating:'text-amber-400 bg-amber-500/10 border-amber-500/30',
  escalated:    'text-rose-400 bg-rose-500/10 border-rose-500/30',
  contained:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  resolved:     'text-zinc-400 bg-zinc-700/20 border-zinc-600/30',
};

const STATUS_LABEL: Record<Status, string> = {
  new: 'New', investigating: 'Investigating', escalated: 'Escalated',
  contained: 'Contained', resolved: 'Resolved',
};

const SEV_ICON: Record<Severity, React.ReactNode> = {
  CRITICAL: <XCircle className="w-4 h-4 text-rose-400" />,
  WARNING:  <AlertTriangle className="w-4 h-4 text-amber-400" />,
  INFO:     <Bell className="w-4 h-4 text-cyan-400" />,
};

const TEAM = ['Maya R.', 'Priya K.', 'Leon M.', 'Arun S.', 'Nisha V.', 'Unassigned'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(ts: string) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtReach(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

// ── Alert Detail Modal ────────────────────────────────────────────────────────
const AlertDetailModal: React.FC<{
  alert: Alert;
  onClose: () => void;
  onStatusChange: (id: string, s: Status) => void;
  onAssign: (id: string, who: string) => void;
}> = ({ alert, onClose, onStatusChange, onAssign }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-zinc-900 border border-zinc-700/60 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 p-5 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
        <div className="flex items-center gap-2 flex-wrap">
          {SEV_ICON[alert.severity]}
          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${SEV_STYLE[alert.severity]}`}>{alert.severity}</span>
          <span className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{alert.type}</span>
          <span className={`text-xs px-2 py-0.5 rounded border font-medium ${STATUS_STYLE[alert.status]}`}>{STATUS_LABEL[alert.status]}</span>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors flex-shrink-0"><X className="w-5 h-5" /></button>
      </div>

      <div className="p-5 space-y-5">
        <p className="text-white text-sm leading-relaxed">{alert.message}</p>

        {/* Risk metrics */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Risk Score', value: String(alert.riskScore), icon: <Shield className="w-3.5 h-3.5" />, color: alert.riskScore >= 80 ? 'text-rose-400' : alert.riskScore >= 60 ? 'text-amber-400' : 'text-emerald-400' },
            { label: 'Velocity', value: `${alert.velocity}/hr`, icon: <Zap className="w-3.5 h-3.5" />, color: 'text-amber-400' },
            { label: 'Reach at Risk', value: fmtReach(alert.reachEstimate), icon: <Eye className="w-3.5 h-3.5" />, color: 'text-violet-400' },
          ].map(m => (
            <div key={m.label} className="bg-zinc-800/60 rounded-xl p-3 border border-zinc-700/40">
              <div className="flex items-center gap-1.5 text-zinc-400 mb-1">{m.icon}<span className="text-xs">{m.label}</span></div>
              <div className={`text-lg font-bold ${m.color}`}>{m.value}</div>
            </div>
          ))}
        </div>

        {/* Platforms */}
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Globe className="w-3 h-3"/>Platforms</p>
          <div className="flex flex-wrap gap-1.5">
            {alert.platforms.map(p => <span key={p} className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{p}</span>)}
          </div>
        </div>

        {/* Contributing signals */}
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><TrendingUp className="w-3 h-3"/>Top Contributing Signals</p>
          <ul className="space-y-1.5">
            {alert.factors.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </div>

        {/* Evidence snippets */}
        {alert.evidenceSnippets.length > 0 && (
          <div>
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Hash className="w-3 h-3"/>Sample Claims Detected</p>
            {alert.evidenceSnippets.map((s, i) => (
              <div key={i} className="bg-zinc-800/50 border border-zinc-700/40 rounded-lg px-3 py-2 text-xs text-zinc-300 italic mb-2">{s}</div>
            ))}
          </div>
        )}

        {/* Recommended actions */}
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Target className="w-3 h-3"/>Recommended Actions</p>
          <ul className="space-y-1.5">
            {alert.recommendedActions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />{a}
              </li>
            ))}
          </ul>
        </div>

        {/* Assignment */}
        <div className="flex items-center gap-3">
          <User className="w-4 h-4 text-zinc-400" />
          <span className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Assign To</span>
          <select
            value={alert.assignee || 'Unassigned'}
            onChange={e => onAssign(alert.id, e.target.value)}
            className="ml-auto bg-zinc-800 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-violet-500/50"
          >
            {TEAM.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Activity log */}
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Clock className="w-3 h-3"/>Activity Log</p>
          <div className="space-y-2 border-l border-zinc-700/50 pl-4">
            {alert.activityLog.map((e, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-5 top-1 w-2 h-2 rounded-full bg-zinc-600" />
                <p className="text-xs text-zinc-400">{timeAgo(e.ts)} · <span className="text-zinc-300 font-medium">{e.actor}</span></p>
                <p className="text-xs text-zinc-400">{e.action}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Status workflow */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-zinc-800">
          <span className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Set Status:</span>
          {(['new', 'investigating', 'escalated', 'contained', 'resolved'] as Status[])
            .filter(s => s !== alert.status)
            .map(s => (
              <button key={s} onClick={() => onStatusChange(alert.id, s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${STATUS_STYLE[s]} hover:opacity-80`}>
                → {STATUS_LABEL[s]}
              </button>
            ))}
        </div>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
export const AlertManagement: React.FC<AlertManagementProps> = ({ monitoringScope }) => {
  const [alerts, setAlerts]               = useState<Alert[]>([]);
  const [loading, setLoading]             = useState(true);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [search, setSearch]               = useState('');
  const [sevFilter, setSevFilter]         = useState<Severity | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter]   = useState<Status | 'ALL'>('ALL');
  const [sortBy, setSortBy]               = useState<'time' | 'severity' | 'risk' | 'velocity'>('time');
  const [selected, setSelected]           = useState<Set<string>>(new Set());

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const norm = (sev: string): Severity =>
      sev === 'CRITICAL' ? 'CRITICAL' : (sev === 'HIGH' || sev === 'MEDIUM' || sev === 'WARNING') ? 'WARNING' : 'INFO';

    (async () => {
      try {
        const data = await apiService.alerts.getAlerts(1, 10, undefined, monitoringScope);
        const raw: any[] = Array.isArray(data) ? data : (data as any).items ?? [];
        if (raw.length > 0) {
          setAlerts(raw.map((a: any) => ({
            id: String(a.id), type: (a.type || 'UNKNOWN').toUpperCase().replace(/_/g,' '),
            severity: norm(a.severity), message: a.message || '',
            platform: a.platform || 'Unknown',
            timestamp: a.timestamp || a.created_at || new Date().toISOString(),
            status: (a.status as Status) || 'new', source: a.source,
            riskScore: Math.round((a.risk_score || 0) * 100), velocity: a.velocity || 0,
            reachEstimate: a.reach_estimate || 0,
            platforms: a.platforms || [a.platform || 'Unknown'],
            assignee: a.assignee,
            recommendedActions: a.recommended_actions || [],
            factors: a.top_factors || [],
            evidenceSnippets: a.evidence_snippets || [],
            activityLog: a.activity_log || [{ ts: a.created_at || new Date().toISOString(), actor: 'System', action: 'Alert triggered' }],
          })));
        } else {
          setAlerts(MOCK_ALERTS);
        }
      } catch { setAlerts(MOCK_ALERTS); }
      finally { setLoading(false); }
    })();
  }, [monitoringScope]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const SEV_ORDER: Record<Severity, number> = { CRITICAL: 0, WARNING: 1, INFO: 2 };
  const filtered = alerts
    .filter(a => sevFilter === 'ALL' || a.severity === sevFilter)
    .filter(a => statusFilter === 'ALL' || a.status === statusFilter)
    .filter(a => !search || a.message.toLowerCase().includes(search.toLowerCase()) || (a.source || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) =>
      sortBy === 'severity' ? SEV_ORDER[a.severity] - SEV_ORDER[b.severity]
      : sortBy === 'risk' ? b.riskScore - a.riskScore
      : sortBy === 'velocity' ? b.velocity - a.velocity
      : new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

  const kpis = {
    total: alerts.length,
    active: alerts.filter(a => a.status === 'new').length,
    investigating: alerts.filter(a => a.status === 'investigating').length,
    escalated: alerts.filter(a => a.status === 'escalated').length,
    contained: alerts.filter(a => a.status === 'contained' || a.status === 'resolved').length,
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStatusChange = useCallback((id: string, status: Status) => {
    const now = new Date().toISOString();
    const patch = (a: Alert) => a.id !== id ? a : { ...a, status, activityLog: [...a.activityLog, { ts: now, actor: 'You', action: `Status → ${STATUS_LABEL[status]}` }] };
    setAlerts(prev => prev.map(patch));
    setSelectedAlert(prev => prev?.id === id ? patch(prev) : prev);
  }, []);

  const handleAssign = useCallback((id: string, who: string) => {
    const now = new Date().toISOString();
    const assignee = who === 'Unassigned' ? undefined : who;
    const patch = (a: Alert) => a.id !== id ? a : { ...a, assignee, activityLog: [...a.activityLog, { ts: now, actor: 'You', action: `Assigned to ${who}` }] };
    setAlerts(prev => prev.map(patch));
    setSelectedAlert(prev => prev?.id === id ? { ...prev, assignee } : prev);
  }, []);

  const toggleSelect = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkStatus = (status: Status) => { selected.forEach(id => handleStatusChange(id, status)); setSelected(new Set()); };

  const exportCSV = () => {
    const rows = [
      ['ID', 'Type', 'Severity', 'Status', 'Risk Score', 'Velocity', 'Reach', 'Platform', 'Message', 'Assignee', 'Timestamp'],
      ...filtered.map(a => [a.id, a.type, a.severity, a.status, a.riskScore, a.velocity, a.reachEstimate, a.platform, `"${a.message.replace(/"/g,'""')}"`, a.assignee || '', a.timestamp]),
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const link = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: 'crisishield-alerts.csv' });
    link.click(); URL.revokeObjectURL(link.href);
  };

  return (
    <div className="p-6 space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Alert Management</h1>
          <p className="text-zinc-400 text-sm mt-1">Triage, investigate, and resolve threat notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 rounded-lg text-sm transition-colors border border-emerald-700/40">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => { setLoading(true); setTimeout(() => { setAlerts(MOCK_ALERTS); setLoading(false); }, 600); }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors border border-zinc-700/50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Strip ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {([
          { label: 'Total', value: kpis.total, color: 'text-zinc-200', icon: <Layers className="w-4 h-4 text-zinc-400" /> },
          { label: 'New', value: kpis.active, color: 'text-violet-400', icon: <Radio className="w-4 h-4 text-violet-400" /> },
          { label: 'Investigating', value: kpis.investigating, color: 'text-amber-400', icon: <Play className="w-4 h-4 text-amber-400" /> },
          { label: 'Escalated', value: kpis.escalated, color: 'text-rose-400', icon: <ArrowUpRight className="w-4 h-4 text-rose-400" /> },
          { label: 'Resolved / Contained', value: kpis.contained, color: 'text-emerald-400', icon: <CheckCircle className="w-4 h-4 text-emerald-400" /> },
        ] as const).map(k => (
          <div key={k.label} className="glass-panel rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">{k.icon}<span className="text-zinc-400 text-xs">{k.label}</span></div>
            <div className={`text-2xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="glass-panel rounded-xl p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input type="text" placeholder="Search alerts by message or source…" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-zinc-800/70 border border-zinc-700/50 text-white rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500/50 placeholder:text-zinc-500" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Filter className="w-4 h-4 text-zinc-500" />
          {(['ALL', 'CRITICAL', 'WARNING', 'INFO'] as const).map(f => (
            <button key={f} onClick={() => setSevFilter(f)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${sevFilter === f ? (f === 'ALL' ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : SEV_STYLE[f as Severity]) : 'bg-zinc-800/50 text-zinc-400 border-zinc-700/50 hover:bg-zinc-700/50'}`}>
              {f === 'ALL' ? 'All Severity' : f}
            </button>
          ))}
          <div className="h-4 w-px bg-zinc-700" />
          {(['ALL', 'new', 'investigating', 'escalated', 'contained', 'resolved'] as const).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${statusFilter === s ? (s === 'ALL' ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : STATUS_STYLE[s as Status]) : 'bg-zinc-800/50 text-zinc-400 border-zinc-700/50 hover:bg-zinc-700/50'}`}>
              {s === 'ALL' ? 'All Status' : STATUS_LABEL[s as Status]}
            </button>
          ))}
          <div className="ml-auto">
            <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
              className="bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-lg px-2 py-1 text-xs focus:outline-none">
              <option value="time">Newest First</option>
              <option value="severity">By Severity</option>
              <option value="risk">By Risk Score</option>
              <option value="velocity">By Velocity</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Bulk Action Bar ─────────────────────────────────────────────────── */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-violet-500/10 border border-violet-500/30 rounded-xl text-sm flex-wrap">
          <span className="text-violet-300 font-semibold">{selected.size} selected</span>
          {(['investigating', 'contained', 'escalated', 'resolved'] as Status[]).map(s => (
            <button key={s} onClick={() => bulkStatus(s)}
              className={`px-3 py-1 rounded-lg text-xs font-medium border ${STATUS_STYLE[s]} hover:opacity-80 transition-opacity`}>
              → {STATUS_LABEL[s]}
            </button>
          ))}
          <button onClick={() => setSelected(new Set())} className="ml-auto text-zinc-400 hover:text-white transition-colors text-xs">Clear</button>
        </div>
      )}

      {/* ── Alert List ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-zinc-800/50 animate-pulse rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="glass-panel rounded-xl p-12 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-white font-semibold">No alerts match your filters</p>
          <p className="text-zinc-400 text-sm mt-1">Try adjusting filters above</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(alert => {
            const isSelected = selected.has(alert.id);
            const borderCls = alert.severity === 'CRITICAL' ? 'border-rose-500/25' : alert.severity === 'WARNING' ? 'border-amber-500/25' : 'border-zinc-700/40';
            return (
              <div key={alert.id}
                className={`glass-panel rounded-xl p-4 border transition-all ${borderCls} ${isSelected ? 'ring-1 ring-violet-500/50' : ''} ${alert.status === 'resolved' || alert.status === 'contained' ? 'opacity-70' : ''}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(alert.id)}
                    className="mt-1 w-4 h-4 rounded accent-violet-500 flex-shrink-0 cursor-pointer" />
                  <div className="mt-0.5 flex-shrink-0">{SEV_ICON[alert.severity]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${SEV_STYLE[alert.severity]}`}>{alert.severity}</span>
                      <span className="text-xs bg-zinc-800 border border-zinc-700 text-zinc-400 px-2 py-0.5 rounded">{alert.type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded border font-medium ${STATUS_STYLE[alert.status]}`}>{STATUS_LABEL[alert.status]}</span>
                      {alert.assignee && (
                        <span className="flex items-center gap-1 text-xs text-zinc-400"><User className="w-3 h-3" />{alert.assignee}</span>
                      )}
                    </div>
                    <p className="text-white text-sm leading-snug">{alert.message}</p>
                    {alert.source && <p className="text-zinc-500 text-xs mt-0.5">Source: {alert.source}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400 flex-wrap">
                      <span>Risk: <span className={`font-bold ${alert.riskScore >= 80 ? 'text-rose-400' : alert.riskScore >= 60 ? 'text-amber-400' : 'text-emerald-400'}`}>{alert.riskScore}</span></span>
                      <span>Velocity: <span className="text-amber-400 font-semibold">{alert.velocity}/hr</span></span>
                      <span>Reach: <span className="text-violet-400 font-semibold">{fmtReach(alert.reachEstimate)}</span></span>
                      <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{alert.platforms.slice(0,2).join(', ')}{alert.platforms.length > 2 ? ` +${alert.platforms.length-2}` : ''}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(alert.timestamp)}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col gap-1.5 items-end">
                    <button onClick={() => setSelectedAlert(alert)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/15 border border-violet-500/30 text-violet-400 hover:bg-violet-500/25 rounded-lg text-xs transition-colors">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    {alert.status === 'new' && (
                      <button onClick={() => handleStatusChange(alert.id, 'investigating')}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/25 text-amber-400 hover:bg-amber-500/20 rounded-lg text-xs transition-colors">
                        <Play className="w-3.5 h-3.5" /> Investigate
                      </button>
                    )}
                    {alert.status === 'investigating' && (
                      <>
                        <button onClick={() => handleStatusChange(alert.id, 'contained')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs transition-colors">
                          <CheckCircle className="w-3.5 h-3.5" /> Contain
                        </button>
                        <button onClick={() => handleStatusChange(alert.id, 'escalated')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/10 border border-rose-500/25 text-rose-400 hover:bg-rose-500/20 rounded-lg text-xs transition-colors">
                          <ArrowUpRight className="w-3.5 h-3.5" /> Escalate
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && (
        <p className="text-zinc-500 text-xs text-right">
          Showing {filtered.length} of {alerts.length} alerts{selected.size > 0 && ` · ${selected.size} selected`}
        </p>
      )}

      {/* ── Detail Modal ────────────────────────────────────────────────────── */}
      {selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onStatusChange={handleStatusChange}
          onAssign={handleAssign}
        />
      )}
    </div>
  );
};

export default AlertManagement;
